import {useCallback} from 'react';
import usePrepareCryptoSetupMutation from 'src/api/graphql/usePrepareCryptoSetupMutation';
import {getStore} from 'src/v2/state/store';
import {
  OpenapiCryptocurrencyPayinsResourceConsumerWalletSessionProviderEnum,
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum,
  OpenapiPostV1CryptoInternalSetupSessionCryptoSetupSessionPrepareInputNetworkEnum,
} from '@stripe-internal/data-v1/schema/types';

interface PrepareSetupParams {
  selectedCurrency: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum;
  walletAddress: string;
  network: OpenapiPostV1CryptoInternalSetupSessionCryptoSetupSessionPrepareInputNetworkEnum;
  walletProvider: OpenapiCryptocurrencyPayinsResourceConsumerWalletSessionProviderEnum;
}

export const usePrepareSessionForSetup = () => {
  const store = getStore();
  const [prepareCryptoSetup] = usePrepareCryptoSetupMutation();
  const sessionId = store.useSelector((state) => state.id);
  const clientSecret = store.useSelector((state) => state.clientSecret);

  return useCallback(
    async (params: PrepareSetupParams) => {
      const response = await prepareCryptoSetup({
        variables: {
          input: {
            client_secret: clientSecret,
            crypto_setup_session: sessionId,
            network: params.network,
            currency: params.selectedCurrency,
            wallet_provider: params.walletProvider,
            wallet_address: params.walletAddress,
          },
        },
      });

      const updatedSession =
        response.data?.v1CryptoInternalSetupSessionCryptoSetupSessionPrepare;

      if (response.error) {
        throw response.error;
      }

      if (!updatedSession) {
        throw new Error('No session returned from prepareCryptoSetup');
      }

      store.setValue((state) => {
        state.state = updatedSession.state;
        state.validBefore = updatedSession.valid_before;
      });
    },
    [sessionId, clientSecret, prepareCryptoSetup, store],
  );
};
